import * as React from 'react'

import { ButtonIcon } from '~ui/components/buttons/ButtonIcon/ButtonIcon'
import { Icon } from '~ui/components/icons/Icon'
import { IconEdit } from '~ui/components/icons/specific/IconEdit'
import { IconNote } from '~ui/components/icons/specific/IconNote'
import { Flex, Grid } from '~ui/components/layout/Box'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'
import { H3 } from '~ui/components/typography/Heading/Heading'
import { Number } from '~ui/components/typography/Number/Number'
import { Text } from '~ui/components/typography/Text/Text'

type TAddon = {
  id: string
  label: string
  amount: number
}

type TCartWareItem = {
  /**
   * unique ware item id
   */
  id: ID
  /**
   * name of the ware item
   */
  nameLabel: string
  /**
   * indicates if it is possible to add addons to the item
   */
  canHaveAddons: boolean
  /**
   * an array of picked addons, empty array if canHaveAddons is false
   */
  addons: RoA<TAddon>
  /**
   * item price with the currency abbreviation
   */
  price: string
  /**
   * item note
   */
  note?: Nullable<string>
}

type TEditableProps =
  | {
      /**
       * if true, the item can be edited, edit button icon is visible and you should provide a click handler as onEdit prop, editable cart idem also shows AmountControl (if provided)
       */
      isCartEditable: true
      /**
       * if true, the item cannot be edited, edit button icon is disabled and you should provide disabled AmountControl (for example by setting amount as a minValue === maxValue of the AmountControl)
       */
      isDisabled?: boolean
      /**
       * amount control component that allows the user to directly change the amount of the item in the cart, rendered only if isEditable === true
       */
      amountControl?: React.ReactNode
      /**
       * click handler of the edit button
       */
      onEdit?: PropsOf<typeof ButtonIcon>['onClick']
    }
  | {
      /**
       * if true, the item can be edited, edit button icon is visible and you should provide a click handler as `onEdit` prop, editable cart item also shows `AmountControl` (if provided)
       */
      isCartEditable?: false
    }

type TProps = NoChildren &
  TEditableProps & {
    /**
     * label to be shown, when there are no addons selected
     */
    noAddonsLabel: string
    /**
     * ware item data to be rendered
     */
    wareItem: TCartWareItem
    /**
     * amount of the item, it is shown in non-editable (read-only) form of the cart, should be synchronized with the amount control component of the editable cart item form
     */
    amount: number
    /**
     * color of the item, bgColor of the Grid container, it's also used to calculate the color of the text
     */
    bgColor?: PropsOf<typeof Grid>['bgColor']
  }

const boldTextBaseProps: PropsOf<typeof Text> & PropsOf<typeof H3> = {
  fontStyle: `bold`,
  fontSize: `m16`,
}

const regularTextBaseProps: PropsOf<typeof Text> = {
  fontSize: `s14`,
  lineHeight: 1.5,
}

/**
 * single item in a Cart (Order)
 */
export const CartItem: React.FC<TProps> = ({
  wareItem,
  bgColor,
  amount,
  noAddonsLabel,
  ...editableProps
}) => {
  return (
    <Grid
      bgColor={bgColor}
      alignItems={`start`}
      justifyItems={`start`}
      rowGap={editableProps.isCartEditable ? `small` : `none`}
      columnGap={`smallMedium`}
      gridTemplateColumns={
        editableProps.isCartEditable && editableProps.amountControl
          ? `max-content 1fr max-content max-content`
          : `max-content 1fr max-content`
      }
    >
      {!editableProps.isCartEditable && (
        <Text {...boldTextBaseProps}>
          <Number>{`${amount}×`}</Number>
        </Text>
      )}
      <Flex
        justifySelf={`stretch`}
        gridColumnStart={editableProps.isCartEditable ? 1 : 2}
        gridColumnEnd={-2}
      >
        <H3 {...boldTextBaseProps}>{wareItem.nameLabel}</H3>
      </Flex>
      <Flex justifySelf={`stretch`} gridColumnEnd={-1}>
        <Text {...boldTextBaseProps}>{wareItem.price}</Text>
      </Flex>

      {/* EDIT BUTTON - rendered if the cart is editable & onEdit handler is defined  */}
      {editableProps.isCartEditable && editableProps.onEdit && (
        <ButtonIcon
          state={editableProps.isDisabled ? `disabled` : `enabled`}
          bgColor={`inputOnPrimary`}
          icon={IconEdit}
          iconColor={`specialColors.info`}
          iconSize={`0.9rem`}
          padding={`smallMedium`}
          onClick={editableProps.onEdit}
        />
      )}

      {wareItem.canHaveAddons && (
        <Flex
          flexDirection={`column`}
          alignSelf={wareItem.addons.length <= 1 ? `center` : `unset`}
          gridColumnStart={2}
        >
          {wareItem.addons.length > 0 ? (
            wareItem.addons.map((addon) => (
              <Text key={addon.id} {...regularTextBaseProps}>
                <Number>{`${addon.amount}×`}</Number>
                {`\u00A0${addon.label}`}
              </Text>
            ))
          ) : (
            <Text {...regularTextBaseProps}>{noAddonsLabel}</Text>
          )}
        </Flex>
      )}

      {editableProps.isCartEditable && editableProps.amountControl && (
        <Flex justifySelf={`stretch`} gridColumnStart={-3} gridColumnEnd={-1}>
          {editableProps.amountControl}
        </Flex>
      )}

      {wareItem.note && (
        <>
          <Grid
            gridColumn={`1`}
            alignSelf={`start`}
            justifySelf={`end`}
            position={`relative`}
            top={`2px`}
          >
            <Icon icon={IconNote} iconSize={`1rem`} contrast={`low`} />
          </Grid>
          <Flex gridColumn={`2/-1`}>
            <Text {...regularTextBaseProps} contrast={`low`}>
              {wareItem.note}
            </Text>
          </Flex>
        </>
      )}
    </Grid>
  )
}

type TSkeletonProps = NoChildren & Pick<TProps, 'isCartEditable'>

/**
 * loading placeholder of the CartItem
 */
export const CartItemSkeleton: React.FC<TSkeletonProps> = ({ isCartEditable }) => {
  return (
    <Grid
      rowGap={`small`}
      alignItems={`start`}
      justifyItems={`start`}
      columnGap={`smallMedium`}
      gridTemplateColumns={
        isCartEditable
          ? `max-content 1fr max-content max-content` //
          : `max-content 1fr max-content`
      }
    >
      {!isCartEditable && (
        <Skeleton>
          <Text {...boldTextBaseProps}>
            <Number>{`X×`}</Number>
          </Text>
        </Skeleton>
      )}
      <Flex gridColumnEnd={-2} justifySelf={`stretch`} gridColumnStart={isCartEditable ? 1 : 2}>
        <Skeleton>
          <H3 {...boldTextBaseProps}>Name label placeholder</H3>
        </Skeleton>
      </Flex>
      <Flex justifySelf={`stretch`} gridColumnEnd={-1}>
        <Skeleton>
          <Text {...boldTextBaseProps}>0 Kč</Text>
        </Skeleton>
      </Flex>
      {isCartEditable && (
        <Skeleton>
          <ButtonIcon
            bgColor={`tertiary`}
            icon={IconEdit}
            radius={`small`}
            iconSize={`0.9rem`}
            padding={`smallMedium`}
          />
        </Skeleton>
      )}

      <Flex flexDirection={`column`} alignSelf={`center`} gridColumnStart={2}>
        <Skeleton height={`16px`}>
          <Text {...regularTextBaseProps}>placeholder</Text>
        </Skeleton>
      </Flex>
    </Grid>
  )
}
